import { Col, Row } from "antd";
import cuid from "cuid";
import dayjs from "dayjs";
import { get, set } from "lodash";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  MdOutlineCalendarToday,
  MdOutlineFrontHand,
  MdOutlinePinDrop,
} from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import { NAME_SPACES } from "shared/locales/constants";
import { acorisMinTerminationDate } from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/terminationRequest";
import {
  API_DATE_FORMAT,
  DATE_FORMAT,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";
import { filterContractNumber } from "utils/helpers/validations";

const TerminationRequest = ({
  initialValues,
  setPercent,
  bindField,
  setDisabled,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });

  const contractMoreThanOneYear = get(
    initialValues,
    "project.fields.contractMoreThanOneYear",
    false,
  );

  const selfOccupation = get(
    initialValues,
    "project.fields.selfOccupation",
    undefined,
  );

  const riaPath = "project.ria";
  const riaLabel =
    SUBSCRIPTION.FORM.EXISTING_CONTRACT?.HAS_EXISTING_CONTRACT ||
    "Le prospect dispose-t-il actuellement d'un contrat de complémentaire santé ?";
  const riaField = bindField(riaPath, {
    label: riaLabel,
    onChange: () => {
      set(initialValues, "project.fields.contractMoreThanOneYear", false);
      set(initialValues, "project.fields.selfOccupation", undefined);
      set(initialValues, "project.terminationRequest", null);
      setPercent(initialValues);
    },
  });

  const contractMoreThanOneYearPath = "project.fields.contractMoreThanOneYear";
  const contractMoreThanOneYearLabel =
    SUBSCRIPTION.FORM.EXISTING_CONTRACT?.CONTRACT_MORE_THAN_YEAR ||
    "Contrat du prospect souscrit depuis plus d'un an";
  const contractMoreThanOneYearField = bindField(contractMoreThanOneYearPath, {
    label: contractMoreThanOneYearLabel,
    onChange: () => {
      set(initialValues, "project.fields.selfOccupation", undefined);
      set(initialValues, "project.terminationRequest", null);
      setPercent(initialValues);
    },
  });

  const selfOccupationPath = "project.fields.selfOccupation";
  const selfOccupationLabel =
    SUBSCRIPTION.FORM.EXISTING_CONTRACT?.DELEGATE_TERMINATION ||
    "L'adhérent souhaite déléguer la résiliation de son contrat";
  const selfOccupationField = bindField(selfOccupationPath, {
    label: selfOccupationLabel,
    extractValue: (checked) => !checked,
    onChange: (selfOccupationValue) => {
      const checked = selfOccupationValue === false;
      if (checked) {
        set(initialValues, "project.terminationRequest", {
          id: cuid(),
          reason: "22",
          brokerageMandate: {
            id: cuid(),
          },
        });
        flushSync(() => {
          setDisabled((prev) => {
            setPercent(initialValues, {
              ...prev,
              project: {
                ...prev.project,
                terminationRequest: undefined,
              },
            });
            return {
              ...prev,
              project: {
                ...prev.project,
                terminationRequest: undefined,
              },
            };
          });
        });
      } else {
        set(initialValues, "project.terminationRequest", null);
        flushSync(() => {
          setDisabled((prev) => {
            const terminationRequestDisabled = {
              dueDate: true,
              contractNumber: true,
              brokerageMandate: {
                currentCompany: true,
              },
            };
            setPercent(initialValues, {
              ...prev,
              project: {
                ...prev.project,
                terminationRequest: terminationRequestDisabled,
              },
            });
            return {
              ...prev,
              project: {
                ...prev.project,
                terminationRequest: terminationRequestDisabled,
              },
            };
          });
        });
      }
    },
  });

  const dueDatePath = "project.terminationRequest.dueDate";
  const dueDateLabel =
    SUBSCRIPTION.FORM.EXISTING_CONTRACT?.CURRENT_EFFECTIVE_DATE?.LABEL ||
    "Date d'effet du contrat actuel du prospect";
  const dueDateField = bindField(dueDatePath, {
    label: dueDateLabel,
    required: true,
    extractValue: (date) =>
      date ? date.set("hour", 4).set("minute", 0).set("second", 0) : date,
    onBlur: () => setPercent(initialValues),
  });

  const terminationDatePath = "project.terminationRequest.terminationDate";
  const terminationDateLabel =
    SUBSCRIPTION.FORM.EXISTING_CONTRACT?.TERMINATION_DATE?.LABEL ||
    "Date de résiliation du contrat actuel";
  const terminationDateField = bindField(terminationDatePath, {
    label: terminationDateLabel,
    required: true,
    extractValue: (date) =>
      date ? date.set("hour", 4).set("minute", 0).set("second", 0) : date,
    onBlur: () => setPercent(initialValues),
  });
  const minTerminationDate = acorisMinTerminationDate();

  const contractNumberPath = "project.terminationRequest.contractNumber";
  const contractNumberLabel =
    SUBSCRIPTION.FORM.EXISTING_CONTRACT?.CONTRACT_NUMBER?.LABEL ||
    "N° de contrat";
  const contractNumberField = bindField(contractNumberPath, {
    label: contractNumberLabel,
    required: true,
    extractValue: ({ target: { value } }) => filterContractNumber(value),
    onBlur: () => setPercent(initialValues),
  });

  const currentCompanyPath =
    "project.terminationRequest.brokerageMandate.currentCompany";
  const currentCompanyLabel =
    SUBSCRIPTION.FORM.EXISTING_CONTRACT?.CURRENT_COMPANY?.LABEL ||
    "Nom de l'assureur actuel du prospect";
  const currentCompanyField = bindField(currentCompanyPath, {
    label: currentCompanyLabel,
    required: true,
    onBlur: () => setPercent(initialValues),
  });

  const DESTINATION =
    SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION?.EXISTING_CONTRACT?.ACORIS || {};

  const destinationAddressPath = "project.terminationRequest.address";
  const destinationAddressLabel =
    DESTINATION.ADDRESS?.LABEL || "Adresse postale de l'assureur actuel";
  const destinationAddressField = bindField(destinationAddressPath, {
    label: destinationAddressLabel,
    required: true,
    onBlur: () => setPercent(initialValues),
  });

  const destinationComplementPath = "project.terminationRequest.complement";
  const destinationComplementLabel =
    DESTINATION.COMPLEMENT?.LABEL || "Complément d'adresse";
  const destinationComplementField = bindField(destinationComplementPath, {
    label: destinationComplementLabel,
    onBlur: () => setPercent(initialValues),
  });

  const destinationCityPath = "project.terminationRequest.city";

  const destinationZipCodePath = "project.terminationRequest.zipCode";
  const destinationZipCodeLabel = DESTINATION.ZIP_CODE?.LABEL || "Code postal";
  const destinationZipCodeField = bindField(destinationZipCodePath, {
    label: destinationZipCodeLabel,
    required: true,
    onBlur: () => setPercent(initialValues),
  });

  const destinationCityLabel = DESTINATION.CITY?.LABEL || "Ville";
  const destinationCityField = bindField(destinationCityPath, {
    label: destinationCityLabel,
    required: true,
    onBlur: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.TERMINATION_REQUEST}-section`}
    >
      <Form
        type="vertical"
        title={
          SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.TERMINATION_REQUEST.TITLE
        }
        subtitle={
          SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.TERMINATION_REQUEST.SUBTITLE
        }
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <FormField
              path={riaField.path}
              label={riaField.label}
              required={riaField.required}
              disabled={riaField.disabled}
            >
              {(field) => (
                <Input
                  type="switch"
                  label={riaLabel}
                  checked={get(initialValues, riaPath, false)}
                  {...field}
                  onChange={riaField.onChange}
                  onBlur={riaField.onBlur}
                />
              )}
            </FormField>
          </Col>

          {get(initialValues, "project.ria", false) && (
            <Col lg={24} xs={24}>
              <FormField
                path={contractMoreThanOneYearField.path}
                label={contractMoreThanOneYearField.label}
                required={contractMoreThanOneYearField.required}
                disabled={contractMoreThanOneYearField.disabled}
              >
                {(field) => (
                  <Input
                    type="switch"
                    label={contractMoreThanOneYearLabel}
                    checked={contractMoreThanOneYear}
                    {...field}
                    onChange={contractMoreThanOneYearField.onChange}
                    onBlur={contractMoreThanOneYearField.onBlur}
                  />
                )}
              </FormField>
            </Col>
          )}

          {get(initialValues, "project.ria", false) &&
            contractMoreThanOneYear && (
              <Col lg={24} xs={24}>
                <FormField
                  path={selfOccupationField.path}
                  label={selfOccupationField.label}
                  required={selfOccupationField.required}
                  disabled={selfOccupationField.disabled}
                >
                  {(field) => (
                    <Input
                      type="switch"
                      label={selfOccupationLabel}
                      checked={selfOccupation === false}
                      {...field}
                      onChange={selfOccupationField.onChange}
                      onBlur={selfOccupationField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
            )}

          {get(initialValues, "project.ria", false) &&
            contractMoreThanOneYear &&
            selfOccupation === false && (
              <>
                <Col lg={24} xs={24}>
                  <FormField
                    path={dueDateField.path}
                    label={dueDateField.label}
                    required={dueDateField.required}
                    disabled={dueDateField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={dueDateLabel}
                        placeholder={
                          SUBSCRIPTION.FORM.EXISTING_CONTRACT
                            ?.CURRENT_EFFECTIVE_DATE?.PLACEHOLDER ||
                          "JJ/MM/AAAA"
                        }
                        type="date"
                        variant="simple"
                        format={DATE_FORMAT}
                        disabledDate={(current) => {
                          return (
                            current && current > dayjs().subtract(1, "year")
                          );
                        }}
                        defaultPickerValue={dayjs().subtract(1, "year")}
                        defaultValue={(() => {
                          const dateValue = get(initialValues, dueDatePath);
                          if (!dateValue) return undefined;
                          const parsedDate = dayjs(
                            dateValue,
                            API_DATE_FORMAT,
                            true,
                          ).isValid()
                            ? dayjs(dateValue, API_DATE_FORMAT, true)
                            : dayjs(dateValue);
                          return parsedDate.isValid() ? parsedDate : undefined;
                        })()}
                        icon={<MdOutlineCalendarToday size={16} />}
                        {...field}
                        onChange={dueDateField.onChange}
                        onBlur={dueDateField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={terminationDateField.path}
                    label={terminationDateField.label}
                    required={terminationDateField.required}
                    disabled={terminationDateField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={terminationDateLabel}
                        placeholder={
                          SUBSCRIPTION.FORM.EXISTING_CONTRACT?.TERMINATION_DATE
                            ?.PLACEHOLDER || "JJ/MM/AAAA"
                        }
                        type="date"
                        variant="simple"
                        format={DATE_FORMAT}
                        disabledDate={(current) =>
                          current && current < minTerminationDate
                        }
                        defaultPickerValue={minTerminationDate}
                        defaultValue={(() => {
                          const dateValue = get(
                            initialValues,
                            terminationDatePath,
                          );
                          if (!dateValue) return undefined;
                          const parsedDate = dayjs(dateValue);
                          return parsedDate.isValid() ? parsedDate : undefined;
                        })()}
                        icon={<MdOutlineCalendarToday size={16} />}
                        {...field}
                        onChange={terminationDateField.onChange}
                        onBlur={terminationDateField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={contractNumberField.path}
                    label={contractNumberField.label}
                    required={contractNumberField.required}
                    disabled={contractNumberField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={contractNumberLabel}
                        placeholder={
                          SUBSCRIPTION.FORM.EXISTING_CONTRACT?.CONTRACT_NUMBER
                            ?.PLACEHOLDER || "ex : 123456789"
                        }
                        icon={<MdOutlineFrontHand size={16} />}
                        type="custom"
                        stripSpaces={true}
                        defaultValue={contractNumberField.defaultValue}
                        {...field}
                        onChange={contractNumberField.onChange}
                        onBlur={contractNumberField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={currentCompanyField.path}
                    label={currentCompanyField.label}
                    required={currentCompanyField.required}
                    disabled={currentCompanyField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={currentCompanyLabel}
                        placeholder={
                          SUBSCRIPTION.FORM.EXISTING_CONTRACT?.CURRENT_COMPANY
                            ?.PLACEHOLDER || "Ex : Allianz"
                        }
                        type="custom"
                        defaultValue={currentCompanyField.defaultValue}
                        {...field}
                        onChange={currentCompanyField.onChange}
                        onBlur={currentCompanyField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={destinationAddressField.path}
                    label={destinationAddressField.label}
                    required={destinationAddressField.required}
                    disabled={destinationAddressField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={destinationAddressLabel}
                        placeholder={
                          DESTINATION.ADDRESS?.PLACEHOLDER ||
                          "ex : 10 Rue de la Liberté"
                        }
                        icon={<MdOutlinePinDrop size={16} />}
                        type="custom"
                        normalizeText={true}
                        defaultValue={destinationAddressField.defaultValue}
                        {...field}
                        onChange={destinationAddressField.onChange}
                        onBlur={destinationAddressField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={destinationComplementField.path}
                    label={destinationComplementField.label}
                  >
                    {(field) => (
                      <Input
                        label={destinationComplementLabel}
                        placeholder={
                          DESTINATION.COMPLEMENT?.PLACEHOLDER ||
                          "ex : Bâtiment B, 2ème étage"
                        }
                        icon={<MdOutlinePinDrop size={16} />}
                        type="custom"
                        normalizeText={true}
                        defaultValue={destinationComplementField.defaultValue}
                        {...field}
                        onChange={destinationComplementField.onChange}
                        onBlur={destinationComplementField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={12} xs={24}>
                  <FormField
                    path={destinationZipCodeField.path}
                    label={destinationZipCodeField.label}
                    required={destinationZipCodeField.required}
                    disabled={destinationZipCodeField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={destinationZipCodeLabel}
                        placeholder={
                          DESTINATION.ZIP_CODE?.PLACEHOLDER || "ex : 69003"
                        }
                        icon={<MdOutlinePinDrop size={16} />}
                        type="custom"
                        defaultValue={destinationZipCodeField.defaultValue}
                        {...field}
                        onChange={destinationZipCodeField.onChange}
                        onBlur={destinationZipCodeField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={12} xs={24}>
                  <FormField
                    path={destinationCityField.path}
                    label={destinationCityField.label}
                    required={destinationCityField.required}
                    disabled={destinationCityField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={destinationCityLabel}
                        placeholder={
                          DESTINATION.CITY?.FREE_PLACEHOLDER || "Ville"
                        }
                        icon={<MdOutlinePinDrop size={16} />}
                        type="custom"
                        defaultValue={destinationCityField.defaultValue}
                        {...field}
                        onChange={destinationCityField.onChange}
                        onBlur={destinationCityField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
              </>
            )}
        </Row>
      </Form>
    </div>
  );
};

export default TerminationRequest;
